// Wrapper enabling left/right swipe actions on mobile.
// @ts-check
import { useDraggable } from '@dnd-kit/core';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { SWIPE_ACTION_THRESHOLD_PX } from '@/config/constants';
import { useT } from '@/i18n/useT';
import { getTaskItemSwipeTeaserSx } from '@/features/tasks/components/items/taskItemSwipeTeaserSx';

const SCALE_FACTOR = 1.4;

/**
 * @param {number} x
 * @param {boolean} swipeTeaserActive
 * @param {boolean} isDragging
 */
function resolveTaskItemSwipeTeaserState(
  x,
  swipeTeaserActive,
  isDragging,
) {
  return {
    showSwipeTeaser: swipeTeaserActive && !isDragging,
    isCompleting: x > 0,
    isSnoozing: x < 0,
    progress: Math.min(Math.abs(x) / SWIPE_ACTION_THRESHOLD_PX, 1),
  };
}

/**
 * @param {import('@dnd-kit/utilities').Transform | null} transform
 */
function getSwipeOffsetX(transform) {
  return transform ? transform.x : 0;
}

/**
 * @param {string} leftSwipeAction
 */
function getSnoozeTheme(leftSwipeAction) {
  return {
    color:
      leftSwipeAction === 'today'
        ? 'var(--color-primary-500)'
        : 'var(--color-warning)',
    Icon: leftSwipeAction === 'today' ? CalendarTodayIcon : ScheduleIcon,
  };
}

/**
 * @param {boolean} isCompleting
 * @param {boolean} isSnoozing
 * @param {string} snoozeColor
 */
function getSwipeBackgroundColor(isCompleting, isSnoozing, snoozeColor) {
  if (isCompleting) return 'var(--color-success)';
  if (isSnoozing) return snoozeColor;
  return '#eee';
}

/**
 * @param {number} x
 * @param {boolean} isDragging
 * @returns {React.CSSProperties}
 */
function buildSwipeContentStyle(x, isDragging) {
  return {
    transform: `translate3d(${x}px, 0, 0)`,
    transition: isDragging ? 'none' : 'transform 200ms ease',
    position: 'relative',
    zIndex: 2,
    backgroundColor: isDragging
      ? 'var(--color-bg-surface, #fff)'
      : 'transparent',
    borderRadius: '4px',
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
  };
}

/**
 * @param {boolean} isCompleting
 * @param {boolean} isSnoozing
 * @param {number} progress
 * @param {string} snoozeColor
 * @returns {React.CSSProperties}
 */
function buildSwipeBackgroundStyle(
  isCompleting,
  isSnoozing,
  progress,
  snoozeColor,
) {
  return {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: isCompleting ? 'flex-start' : 'flex-end',
    alignItems: 'center',
    padding: '0 20px',
    color: 'white',
    zIndex: 1,
    borderRadius: '4px',
    opacity: Math.min(progress * SCALE_FACTOR, 1),
    backgroundColor: getSwipeBackgroundColor(
      isCompleting,
      isSnoozing,
      snoozeColor,
    ),
  };
}

/**
 * @param {boolean} isCompleting
 * @param {number} progress
 * @param {(key: string, fallback: string) => string} t
 */
function renderCompleteAction(isCompleting, progress, t) {
  if (!isCompleting) return null;
  const iconScale = Math.min(progress * SCALE_FACTOR, 1);
  return (
    <>
      <CheckCircleIcon
        sx={{
          mr: 1,
          fontSize: '1.5rem',
          transform: `scale(${iconScale})`,
          transition: 'transform 60ms linear',
        }}
      />
      <Typography variant="button" sx={{ fontWeight: 'bold' }}>
        {t('taskitemswipeable.complete', 'Complete')}
      </Typography>
    </>
  );
}

/**
 * @param {boolean} isSnoozing
 * @param {string} leftSwipeAction
 * @param {(key: string, fallback: string) => string} t
 * @param {typeof ScheduleIcon} SnoozeIcon
 */
function renderSnoozeAction(isSnoozing, leftSwipeAction, t, SnoozeIcon) {
  if (!isSnoozing) return null;
  const snoozeLabel =
    leftSwipeAction === 'today'
      ? t('taskitemswipeable.snoozeToday', 'Today')
      : t('taskitemswipeable.snoozeTomorrow', 'Tomorrow');
  return (
    <>
      <Typography variant="button" sx={{ mr: 1 }}>
        {snoozeLabel}
      </Typography>
      <SnoozeIcon />
    </>
  );
}

/**
 * @param {{
 *   task: Task,
 *   leftSwipeAction?: string,
 *   swipeTeaserActive?: boolean,
 *   onSwipeTeaserEnd?: () => void,
 *   children: React.ReactNode
 * }} props
 */
const TaskItemSwipeable = ({
  task,
  leftSwipeAction = 'tomorrow',
  swipeTeaserActive = false,
  onSwipeTeaserEnd,
  children,
}) => {
  const t = useT();
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `swipe-${task.id}`,
      data: {
        type: 'swipeable-task',
        task,
        leftSwipeAction,
      },
    });

  const x = getSwipeOffsetX(transform);
  const { showSwipeTeaser, isCompleting, isSnoozing, progress } =
    resolveTaskItemSwipeTeaserState(x, swipeTeaserActive, isDragging);
  const { color: snoozeColor, Icon: SnoozeIcon } =
    getSnoozeTheme(leftSwipeAction);

  return (
    <Box sx={{ position: 'relative', borderRadius: '4px' }}>
      <div
        style={buildSwipeBackgroundStyle(
          isCompleting,
          isSnoozing,
          progress,
          snoozeColor,
        )}
      >
        {renderCompleteAction(isCompleting, progress, t)}
        {renderSnoozeAction(isSnoozing, leftSwipeAction, t, SnoozeIcon)}
      </div>
      <Box
        data-testid={showSwipeTeaser ? 'swipe-teaser-active' : undefined}
        onAnimationEnd={showSwipeTeaser ? onSwipeTeaserEnd : undefined}
        sx={showSwipeTeaser ? getTaskItemSwipeTeaserSx() : undefined}
      >
        <div
          ref={setNodeRef}
          {...listeners}
          {...attributes}
          style={buildSwipeContentStyle(x, isDragging)}
        >
          {children !== null ? children : null}
        </div>
      </Box>
    </Box>
  );
};

export default TaskItemSwipeable;
